import React, { useState, useEffect } from 'react';
import styles from './Certifications.module.css';

const Certifications = ({ description = false, background = false, scroll = true }) => {
  const [certifications, setCertifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCertifications = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${process.env.REACT_APP_CMS_BASE_URL}/api/certification-logo`);

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        // Transform API data to match component structure
        const transformedCertifications = data.results.map((cert) => ({
          id: cert.id,
          src: `${process.env.REACT_APP_CMS_BASE_URL}/attachments/${cert.logo}`,
          alt: cert.name,
          slug: cert.slug
        }));

        setCertifications(transformedCertifications);
        setError(null);
      } catch (err) {
        console.error('Error fetching certifications:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchCertifications();
  }, []);

  const handleImageError = (e) => {
    console.error('Failed to load image:', e.target.src);
    e.target.style.display = 'none';
  };

  // Calculate how many sets we need to avoid visible duplicates (only if scrolling)
  const createScrollingCertifications = () => {
    if (certifications.length === 0) return [];

    // If not scrolling, just return original certifications
    if (!scroll) return certifications;

    // Estimate viewport width and icon width to determine minimum duplicates needed
    const iconWidth = 150; // 9.375rem ≈ 150px
    const gapWidth = 64; // 4rem ≈ 64px
    const itemTotalWidth = iconWidth + gapWidth;
    const viewportWidth = window.innerWidth || 1200;

    // Calculate how many icons fit in viewport
    const iconsInViewport = Math.ceil(viewportWidth / itemTotalWidth);

    // We need at least 3 times the viewport worth to avoid seeing duplicates
    const minDuplicates = Math.max(3, Math.ceil((iconsInViewport * 3) / certifications.length));

    // Create the duplicated array
    const result = [];
    for (let i = 0; i < minDuplicates; i++) {
      result.push(...certifications);
    }

    return result;
  };

  if (loading) {
    return (
      <section className={styles.certificationsSection}>
        <div className={styles.stateContainer}>
          <div className={styles.loadingState}>
            <p className={styles.stateMessage}>Loading certifications...</p>
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className={styles.certificationsSection}>
        <div className={styles.stateContainer}>
          <div className={styles.errorState}>
            <p className={styles.stateMessage}>Error loading certifications: {error}</p>
          </div>
        </div>
      </section>
    );
  }

  if (certifications.length === 0) {
    return (
      <section className={styles.certificationsSection}>
        <div className={styles.stateContainer}>
          <div className={styles.emptyState}>
            <p className={styles.stateMessage}>No certifications available</p>
          </div>
        </div>
      </section>
    );
  }

  const scrollingCertifications = createScrollingCertifications();

  return (
    <section className={`${styles.certificationsSection} ${background ? styles.withBackground : ''}`}>
      {/* Content Container */}
      <div className={styles.contentContainer}>

        {/* Certifications Display */}
        <div className={styles.certificationsWrapper}>
          <div className={`${styles.certificationsDisplay} ${scroll ? styles.scrollingMode : styles.staticMode}`}>
            <div className={styles.certificationsContainer}>
              {scrollingCertifications.map((cert, index) => (
                <div
                  className={styles.certificationItem}
                  key={scroll ? `${cert.id}-${Math.floor(index / certifications.length)}-${index % certifications.length}` : cert.id}
                >
                  <img
                    src={cert.src}
                    alt={cert.alt}
                    className={styles.certificationImage}
                    onError={handleImageError}
                    loading="lazy"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Description Section */}
        {description && (
          <div className={styles.descriptionWrapper}>
            <p className={styles.descriptionText}>
              Ensuring highest standards of security and safety, with secure firmware development that is digitally signed and equipped with advanced security measures to guarantee safe and efficient operation across every mission.
            </p>
          </div>
        )}
      </div>
    </section>
  );
};

export default Certifications;